import type {SystemStyleObject} from '@chakra-ui/react';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {useMultiStyleConfig} from '@chakra-ui/system';

const parts = [`root`, `icon`, `message`, `title`, `description`, `actions`, `retry`, `close`] as const;

type Parts = (typeof parts)[number];

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts);

export const toastTheme = defineMultiStyleConfig({
  baseStyle: definePartsStyle(props => {
    const {orientation} = props;

    return {
      root: {
        display: `flex`,
        alignItems: `center`,
        textAlign: `start`,
        width: `auto`,
        borderRadius: `radius-100`,
        gap: `space-100`,
        position: `relative`,
        bgColor: `background.primary-inverse`,
        color: `text.primary-inverse`,
        p: `space-150`,
        minH: `3.5rem`,
      },
      icon: {
        width: `1.5rem`,
        height: `1.5rem`,
        _empty: {
          display: `none`,
        },
      },
      message: {
        flex: `1`,
        display: `flex`,
        maxWidth: `100%`,
        gap: `space-100`,
        flexDirection: orientation === `horizontal` ? `row` : `column`,
        width: `100%`,
        overflow: `hidden`,
      },
      title: {
        color: `inherit`,
        overflow: `hidden`,
        textOverflow: `ellipsis`,
        whiteSpace: `nowrap`,
        width: `100%`,
      },
      description: {
        color: `inherit`,
      },
      actions: {
        display: `flex`,
        alignItems: `center`,
      },
      retry: {
        px: `space-200`,
        height: `2rem`,
        py: `space-050`,
        fontSize: `sm`,
        lineHeight: `6`,
      },
      close: {
        width: `2rem`,
        height: `2rem`,
      },
    };
  }),
  sizes: {
    lg: definePartsStyle({
      root: {
        px: `space-400`,
        py: `space-200`,
        borderRadius: `radius-200`,
      },
    }),
    md: definePartsStyle({
      root: {
        px: `space-200`,
        py: `space-150`,
      },
    }),
    sm: definePartsStyle({
      root: {
        px: `space-150`,
        py: `space-050 `,
      },
    }),
  },
  defaultProps: {
    size: `md`,
  },
});

export const useToastStyleConfig = (
  props?: Parameters<typeof useMultiStyleConfig>[1],
): Record<Parts, SystemStyleObject> => useMultiStyleConfig(`Toast`, props ?? {});
